/**
 * Hooks do RAZÃO FINANCEIRO com integração bancária — espelham
 * `FinancialLedgerDto` de treasury.dto.ts.
 *
 * Contrato consumido (served-routes.json):
 *   GET /finance/ledger?side&statusIn&q&page&pageSize → FinancialLedgerDto
 *   (query: LedgerQueryDto)
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';

/** treasury.dto.ts: FinancialLedgerDto (item do razão) */
export interface LedgerItem {
  id: string;
  side: 'PAYABLE' | 'RECEIVABLE';
  partyId: string;
  partyName: string;
  documentNumber: string;
  originalValue: number;
  balance: number;
  originalDueDate: string;
  currentDueDate: string;
  paymentMethod: string | null;
  status: string;
  bankingStatus: string;
  scheduledPaymentId: string | null;
  scheduledBatchId: string | null;
}

/** treasury.dto.ts: FinancialLedgerDto */
export interface FinancialLedger {
  items: LedgerItem[];
  total: number;
  summary: {
    totalPayables: number;
    totalReceivables: number;
    scheduledCount: number;
  };
}

/** treasury.dto.ts: LedgerQueryDto */
export interface LedgerFilters {
  side?: 'PAYABLE' | 'RECEIVABLE';
  statusIn?: string[];
  q?: string;
  page?: number;
  pageSize?: number;
}

const ledgerKeys = {
  list: (filters: LedgerFilters) => ['finance', 'treasury', 'ledger', filters] as const,
};

export function useLedger(filters: LedgerFilters = {}) {
  return useQuery({
    queryKey: ledgerKeys.list(filters),
    queryFn: () =>
      apiClient.get<FinancialLedger>('/finance/ledger', {
        params: {
          page: String(filters.page ?? 1),
          pageSize: String(filters.pageSize ?? 50),
          ...(filters.side ? { side: filters.side } : {}),
          ...(filters.statusIn && filters.statusIn.length > 0 ? { statusIn: filters.statusIn } : {}),
          ...(filters.q ? { q: filters.q } : {}),
        },
      }),
  });
}

/* ── Rótulos de apresentação (derivados) ── */

export const LEDGER_SIDE_LABEL: Record<LedgerItem['side'], string> = {
  PAYABLE: 'A pagar',
  RECEIVABLE: 'A receber',
};

export const BANKING_STATUS_LABEL: Record<string, string> = {
  NONE: 'Sem integração',
  SCHEDULED: 'Agendado',
  TRANSMITTED: 'Transmitido',
  CONFIRMED: 'Sincronizado',
  FAILED: 'Erro na remessa',
  CANCELLED: 'Cancelado',
};

export function bankingStatusVariant(status: string): 'success' | 'warning' | 'error' | 'info' | 'neutral' {
  if (status === 'CONFIRMED') return 'success';
  if (status === 'FAILED') return 'error';
  if (status === 'TRANSMITTED' || status === 'SCHEDULED') return 'warning';
  if (status === 'NONE') return 'neutral';
  return 'info';
}